/**
 * ZIP 下載 — 依分組（流程變量 / 狀態）建立目錄結構
 */

import JSZip from 'jszip';
import { TimezoneUtils } from '../../../../utils/timezoneUtils';
import { filterGroupedMediaBySelection } from './filterExcludedMedia';
import {
  buildGroupFolderName,
  fetchImageData,
  getFileBaseName,
  triggerBlobDownload,
} from './exportMediaShared';

const uniqueZipPath = (usedPaths, folderPath, baseName, ext) => {
  let candidate = `${folderPath}/${baseName}.${ext}`;
  let index = 1;
  while (usedPaths.has(candidate)) {
    candidate = `${folderPath}/${baseName}_${index}.${ext}`;
    index += 1;
  }
  usedPaths.add(candidate);
  return candidate;
};

/**
 * 導出圖片畫廊為 ZIP（目錄：分組 PV → 狀態子組 → 圖片檔）
 */
export const exportGalleryToZip = async (
  groupedMedia,
  settings = {},
  fileName = 'apps_media_reports',
  onProgress = null,
  groupingPvs = [],
  selectedByGroup = null,
  pvDefinitions = [],
  groupingEformFields = []
) => {
  const mediaForExport = selectedByGroup && Object.keys(selectedByGroup).length > 0
    ? filterGroupedMediaBySelection(groupedMedia, selectedByGroup)
    : groupedMedia;

  if (!mediaForExport?.length) {
    throw new Error('沒有可導出的媒體');
  }

  const zip = new JSZip();
  const usedPaths = new Set();

  const totalSubGroups = mediaForExport.reduce(
    (sum, group) => sum + (group.subGroups?.length || 0),
    0
  );
  let processedSubGroups = 0;

  for (let gi = 0; gi < mediaForExport.length; gi++) {
    const group = mediaForExport[gi];
    const groupFolder = buildGroupFolderName(group, groupingPvs, gi, pvDefinitions, groupingEformFields);

    for (const subGroup of group.subGroups || []) {
      if (onProgress) {
        onProgress(
          processedSubGroups,
          totalSubGroups,
          `正在打包: ${groupFolder}`
        );
      }

      const mediaFiles = subGroup.mediaFiles || [];

      for (const mediaFile of mediaFiles) {
        try {
          const { buffer, ext } = await fetchImageData(mediaFile.filePath);
          const baseName = getFileBaseName(mediaFile.filePath || mediaFile.fileName);
          const zipPath = uniqueZipPath(usedPaths, groupFolder, baseName, ext);
          zip.file(zipPath, buffer);
        } catch (error) {
          console.error(`ZIP 圖片處理失敗: ${mediaFile.filePath}`, error);
        }
      }

      processedSubGroups += 1;
    }
  }

  if (usedPaths.size === 0) {
    throw new Error('沒有可打包的圖片');
  }

  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
  const zipFileName = `${fileName}_${today}.zip`;
  triggerBlobDownload(blob, zipFileName);

  if (onProgress) {
    onProgress(totalSubGroups, totalSubGroups, '打包完成');
  }

  return { success: true, fileName: zipFileName };
};

export default {
  exportGalleryToZip,
};
